import React, { useCallback, useEffect, useState } from "react";

import moment from "moment";

import { getCreditPurchase, updateCreditPurchase } from "services";

import { getShapefileRegions, useCancelAsyncOnUnmount, useNavigate, useParams } from "utils";

import { AdminHead, AdminSection, Button, Checkbox, Col, DatePicker, Form, InputNumber, MainPage, Option, Radio, Row, Select, TextArea } from "components";

export const CreditPurchaseUpdate = () => {
  const { id } = useParams();

  const signal = useCancelAsyncOnUnmount()();
  const navigate = useNavigate();

  const [creditPurchase, setCreditPurchase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regionEnums, setRegionEnums] = useState(true);

  const loadFieldShapeRegions = useCallback(() => {
    getShapefileRegions({ signal }).then((regionEnums) => {
      setRegionEnums(regionEnums);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCreditPurchase = useCallback(() => {
    getCreditPurchase({ id, signal })
      .then((data) => {
        setCreditPurchase(data);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id, signal]);

  const complete = (v) => {
    updateCreditPurchase({ id, ...v, signal })
      .then(() => {
        navigate(`/partners/update/${creditPurchase.partnerId}`);
      })
      .finally(() => {});
  };
  const incomplete = () => {};

  useEffect(() => {
    loadFieldShapeRegions();
    fetchCreditPurchase();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <MainPage loading={loading} pageName={"admin"}>
      <AdminHead
        headline="Update credit purchase"
        description="Update a credit purchase here."
        tooltipText="Back to partner"
        link={`/partners/update/${creditPurchase && creditPurchase.partnerId}`}
      />

      {creditPurchase && (
        <>
          <Row>
            <Col>
              <Form className="midi-form" name="confirm" onFinish={(v) => complete(v)} onFinishFailed={(v) => incomplete(v)}>
                <AdminSection headline="Add credit purchase" />
                <Row>
                  <Col sm={24} md={12}>
                    <InputNumber initialValue={creditPurchase.quantity} name="quantity" label="Quantity" rules={[{ required: true }]} />
                  </Col>
                  <Col sm={24} md={12}>
                    <InputNumber initialValue={creditPurchase.price} name="price" label="Price per-credit" rules={[{ required: true }]} />
                  </Col>
                </Row>

                <Row>
                  <Col sm={24} md={12}>
                    <DatePicker
                      initialValue={moment(creditPurchase.purchasedAt)}
                      name="purchasedAt"
                      label="Purchase date"
                      rules={[{ required: true }]}
                      format={["DD.MM.YYYY"]}
                      showNow={false}
                    />
                  </Col>
                  <Col sm={24} md={12}>
                    <div className="margin-bottom-75 grey-text paragraph-s">Type</div>
                    <Form.Item initialValue={creditPurchase.future} name="future" rules={[{ required: true }]} className="no-suffix">
                      <Radio.Group>
                        <Radio value={false}>Past</Radio>
                        <Radio value={true}>Future</Radio>
                      </Radio.Group>
                    </Form.Item>
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <TextArea initialValue={creditPurchase.notes} name="notes" label="Notes" rules={[{ required: true }]} rows={4} />
                  </Col>
                </Row>

                <Row>
                  <Col sm={24} md={12}>
                    <Select name="creditType" label="Credit Type" rules={[{ required: true }]} initialValue={creditPurchase.creditType}>
                      <Option value={"Insetting"}>Insetting</Option>
                      <Option value={"Offsetting"}>Offsetting</Option>
                    </Select>
                  </Col>
                  <Col sm={24} md={12}>
                    <Form.Item name="prePurchase" initialValue={creditPurchase.prePurchase} valuePropName="checked">
                      <Checkbox>pre-purchase</Checkbox>
                    </Form.Item>
                  </Col>
                </Row>

                <Row>
                  <Col sm={24} md={12}>
                    <DatePicker
                      name="deliveryPeriodStart"
                      label="Delivery Period Start"
                      rules={[{ required: true }]}
                      format={["DD.MM.YYYY"]}
                      showNow={false}
                      initialValue={moment(creditPurchase.deliveryPeriodStart)}
                    />
                  </Col>
                  <Col sm={24} md={12}>
                    <DatePicker
                      name="deliveryPeriodEnd"
                      label="Delivery Period End "
                      rules={[{ required: true }]}
                      format={["DD.MM.YYYY"]}
                      showNow={false}
                      initialValue={moment(creditPurchase.deliveryPeriodEnd)}
                    />
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <TextArea initialValue={creditPurchase.description} name="description" label="Description" rules={[{ required: true }]} rows={2} />
                  </Col>
                </Row>

                <Row>
                  <Col sm={12} md={12}>
                    <TextArea initialValue={creditPurchase.attachments} name="attachments" label="Attachments" rules={[{ required: true }]} rows={1} />
                  </Col>
                  <Col sm={12} md={12}>
                    <TextArea initialValue={creditPurchase.dealId} name="dealId" label="Deal Id" rules={[{ required: true }]} rows={1} />
                  </Col>
                </Row>

                <Row>
                  <Col sm={24} md={12}>
                    <InputNumber
                      initialValue={creditPurchase.productsWithKlimLabel}
                      name="productsWithKlimLabel"
                      label="Products With Klim Label"
                      rules={[{ required: true }]}
                    />
                  </Col>
                  <Col sm={24} md={12}>
                    <InputNumber initialValue={creditPurchase.desiredVintage} name="desiredVintage" label="Desired Vintage" rules={[{ required: true }]} />
                  </Col>
                </Row>

                <Row>
                  <Col sm={24} md={12}>
                    <Select name="desiredRegions" label="Desired Regions" mode="tags" rules={[{ required: true }]} initialValue={creditPurchase.desiredRegions}>
                      {Object.keys(regionEnums).map((item, i) => (
                        <Option value={regionEnums[item]} key={i}>
                          {regionEnums[item]}
                        </Option>
                      ))}
                    </Select>
                  </Col>
                  <Col sm={24} md={12}>
                    <Select name="desiredProgram" label="Desired Program" rules={[{ required: true }]} initialValue={creditPurchase.desiredProgram}>
                      <Option value={"Method"}>Method</Option>
                      <Option value={"Soil+"}>Soil+</Option>
                    </Select>
                  </Col>
                </Row>

                <Row>
                  <Col sm={24} md={12}>
                    <TextArea
                      initialValue={creditPurchase.issuanceRegistryId}
                      name="issuanceRegistryId"
                      label="Issuance Registry Id"
                      rules={[{ required: true }]}
                      rows={1}
                    />
                  </Col>
                  <Col sm={24} md={12}>
                    <DatePicker
                      name="retirementDate"
                      label="Retirement Date"
                      rules={[{ required: true }]}
                      format={["DD.MM.YYYY"]}
                      showNow={false}
                      initialValue={moment(creditPurchase.retirementDate)}
                    />
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <TextArea
                      initialValue={creditPurchase.retirementReason}
                      name="retirementReason"
                      label="Retirement Reason"
                      rules={[{ required: true }]}
                      rows={1}
                    />
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <Form.Item>
                      <Button type="primary" htmlType="submit" disabled={loading} loading={loading}>
                        Update
                      </Button>
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </Col>
          </Row>
        </>
      )}
    </MainPage>
  );
};
